import {
    useCallback,
    useContext,
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    ScrollView,
    Platform,
    View
} from "react-native";
import type ITableProps from "./type";
import type {
    TableContextType
} from "./type";
import stylesheet, {
    getTableMeasures,
    useStyles
} from "./stylesheet";
import TableBody from "./components/body";
import TableCaption from "./components/caption";
import TableCell from "./components/cell";
import TableFooter from "./components/footer";
import TableHead from "./components/head";
import TableHeader from "./components/header";
import TableRow from "./components/row";
import {
    TableContext
} from "./context";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useReducedMotion
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    CardContentFlushContext,
    CardContext
} from "../card/context";

const Table: FC<ITableProps> = ({
    isFirstColumnSticky = false,
    isCaptionHidden = false,
    isHeaderSticky = false,
    isRaised = false,
    size = "medium",
    customTheme,
    children,
    caption,
    style,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        surfaceColor,
        touchTargets,
        durations,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const isReduceMotionEnabled = useReducedMotion();

    const scrollerRef = useRef<View | null>(null);

    const [
        isScrolledToEnd,
        setIsScrolledToEnd
    ] = useState(false);

    const [
        isScrolledFromStart,
        setIsScrolledFromStart
    ] = useState(false);

    const [
        contentHeight,
        setContentHeight
    ] = useState(0);

    const [
        scrollerWidth,
        setScrollerWidth
    ] = useState(0);

    const [
        contentWidth,
        setContentWidth
    ] = useState(0);

    const [
        shrinkWidths,
        setShrinkWidths
    ] = useState<Record<number, number>>({});

    const shrinkCellsRef = useRef<Map<number, Map<string, number>>>(new Map());

    const reportShrinkWidth = useCallback((columnIndex: number, cellID: string, width?: number) => {
        const column = shrinkCellsRef.current.get(columnIndex) ?? new Map<string, number>();

        if(width === undefined) {
            column.delete(cellID);
        } else {
            column.set(cellID, width);
        }

        shrinkCellsRef.current.set(columnIndex, column);

        const columnWidth = Math.max(0, ...column.values());

        setShrinkWidths((current) => current[columnIndex] === columnWidth ? current : {
            ...current,
            [columnIndex]: columnWidth
        });
    }, []);

    const measures = useMemo(() => getTableMeasures({
        controlPaddings,
        controlHeights,
        touchTargets,
        pointerType,
        spaces,
        size
    }), [
        controlPaddings,
        controlHeights,
        touchTargets,
        pointerType,
        spaces,
        size
    ]);

    const cardContext = useContext(CardContext);

    const tableBackground = isRaised ? colors.content.container.raised : colors.content.container[surfaceColor];

    const isInCardContent = !!cardContext && !isRaised;

    const isInFlushContent = useContext(CardContentFlushContext);

    const isFlushInCard = isInCardContent && !isInFlushContent;

    const tableMeasures = useMemo(() => isInCardContent && cardContext ? {
        ...measures,
        inset: cardContext.inset
    } : measures, [
        isInCardContent,
        cardContext,
        measures
    ]);

    const isOverflowing = contentWidth > scrollerWidth + 1;

    const {
        scroller: scrollerDynamicStyle,
        caption: captionDynamicStyle,
        content: contentDynamicStyle,
        fade: fadeDynamicStyle
    } = useStyles({
        isFadeVisible: isOverflowing && !isScrolledToEnd,
        background: tableBackground,
        isReduceMotionEnabled,
        contentHeight,
        isOverflowing,
        direction,
        durations,
        colors,
        spaces
    });

    const context: TableContextType = useMemo(() => ({
        isScrolledFromStart: isFirstColumnSticky && isScrolledFromStart,
        measures: tableMeasures,
        isFirstColumnSticky,
        reportShrinkWidth,
        tableBackground,
        isHeaderSticky,
        shrinkWidths,
        customTheme,
        size
    }), [
        isScrolledFromStart,
        isFirstColumnSticky,
        reportShrinkWidth,
        tableBackground,
        isHeaderSticky,
        shrinkWidths,
        customTheme,
        tableMeasures,
        size
    ]);

    const handleScroll = useCallback(() => {
        const scroller = scrollerRef.current as unknown as HTMLElement | null;

        if(!scroller) {
            return;
        }

        setIsScrolledFromStart(Math.abs(scroller.scrollLeft) > 0);

        setIsScrolledToEnd(scroller.scrollWidth - scroller.clientWidth - Math.abs(scroller.scrollLeft) <= 1);
    }, []);

    useEffect(() => {
        const scroller = scrollerRef.current as unknown as HTMLElement | null;

        if(Platform.OS !== "web" || !scroller) {
            return;
        }

        handleScroll();

        scroller.addEventListener("scroll", handleScroll, {
            passive: true
        });

        return () => {
            scroller.removeEventListener("scroll", handleScroll);
        };
    }, [
        handleScroll,
        contentWidth,
        scrollerWidth
    ]);

    const handleScrollerLayout = (event: LayoutChangeEvent) => {
        setScrollerWidth(Math.round(event.nativeEvent.layout.width));
    };

    const handleContentLayout = (event: LayoutChangeEvent) => {
        setContentWidth(Math.round(event.nativeEvent.layout.width));

        setContentHeight(Math.round(event.nativeEvent.layout.height));

        props.onLayout?.(event);
    };

    const renderTable = () => {
        return <View
            {...props}
            onLayout={handleContentLayout}
            style={[
                stylesheet.content,
                contentDynamicStyle
            ]}
            aria-label={caption}
            role="table"
        >
            {children}
        </View>;
    };

    const renderScroller = () => {
        if(Platform.OS === "web") {
            return <View
                style={stylesheet.scrollerFrame}
            >
                <View
                    onLayout={handleScrollerLayout}
                    style={[
                        stylesheet.scroller,
                        scrollerDynamicStyle
                    ]}
                    ref={scrollerRef}
                >
                    {renderTable()}
                </View>
                {
                    isOverflowing ?
                        <View
                            style={[
                                stylesheet.fade,
                                fadeDynamicStyle
                            ]}
                            aria-hidden={true}
                        />
                    :
                        null
                }
            </View>;
        }

        return <ScrollView
            contentContainerStyle={stylesheet.nativeContent}
            showsHorizontalScrollIndicator={false}
            horizontal={true}
        >
            {renderTable()}
        </ScrollView>;
    };

    const renderCaption = () => {
        if(!caption || isCaptionHidden) {
            return null;
        }

        return <TableCaption
            style={captionDynamicStyle}
        >
            {caption}
        </TableCaption>;
    };

    return <TableContext.Provider
        value={context}
    >
        <View
            style={[
                style,
                stylesheet.container,
                isFlushInCard ? {
                    marginHorizontal: -tableMeasures.inset
                } : undefined
            ]}
        >
            {renderScroller()}
            {renderCaption()}
        </View>
    </TableContext.Provider>;
};
export default Object.assign(Table, {
    Caption: TableCaption,
    Footer: TableFooter,
    Header: TableHeader,
    Body: TableBody,
    Cell: TableCell,
    Head: TableHead,
    Row: TableRow
});
